import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Ticket,
  Plus,
  Search,
  Filter,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  User,
  Calendar,
  Tag,
  MessageSquare,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  RefreshCw,
  Send,
  Edit,
  Trash2,
  AlertCircle,
  Activity,
  PauseCircle,
  PlayCircle,
  RotateCcw,
  FileText,
  Server,
  Wifi,
  Shield,
  Mail,
  Key,
  HelpCircle,
  Lightbulb,
  History,
  Users,
  BarChart3,
  TrendingUp,
  TrendingDown,
  X,
  Download,
  PieChart,
  Target
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TicketDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [workload, setWorkload] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // SLA Report state
  const [slaReport, setSlaReport] = useState(null);
  const [slaReportLoading, setSlaReportLoading] = useState(false);
  const [slaReportPeriod, setSlaReportPeriod] = useState('month');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Create ticket form
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    description: '',
    ticketType: 'incident',
    priority: 'medium',
    category: 'other',
    assigneeId: ''
  });
  
  // Comment form
  const [commentText, setCommentText] = useState('');
  const [isPrivateNote, setIsPrivateNote] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cset_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    fetchStats();
    fetchTickets();
    fetchTeamMembers();
    fetchWorkload();
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [statusFilter, priorityFilter, typeFilter, searchTerm]);
  
  useEffect(() => {
    if (activeTab === 'reports') {
      fetchSlaReport();
    }
  }, [activeTab, slaReportPeriod]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tickets/stats`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/tickets?`;
      if (statusFilter) url += `status=${statusFilter}&`;
      if (priorityFilter) url += `priority=${priorityFilter}&`;
      if (typeFilter) url += `ticketType=${typeFilter}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      const data = await response.json();
      setTickets(data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tickets/team/members`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setTeamMembers(data.members || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const fetchWorkload = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tickets/team/workload`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setWorkload(data.workload || []);
    } catch (error) {
      console.error('Failed to fetch workload:', error);
    }
  };

  const fetchSlaReport = async () => {
    setSlaReportLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets/reports/sla-compliance?period=${slaReportPeriod}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setSlaReport(data);
    } catch (error) {
      console.error('Failed to fetch SLA report:', error);
    } finally {
      setSlaReportLoading(false);
    }
  };

  const handleDownloadSlaReport = async () => {
    setDownloadingPdf(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets/reports/sla-compliance/download?period=${slaReportPeriod}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('cset_token')}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SLA_Compliance_Report_${slaReportPeriod}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Failed to download report:', error);
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const fetchTicketDetail = async (ticketId) => {
    try {
      const response = await fetch(`${API_URL}/api/tickets/${ticketId}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setSelectedTicket(data);
      setShowTicketModal(true);
    } catch (error) {
      console.error('Failed to fetch ticket detail:', error);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(ticketForm)
      });
      const data = await response.json();
      if (data.success) {
        setShowCreateModal(false);
        setTicketForm({
          subject: '',
          description: '',
          ticketType: 'incident',
          priority: 'medium',
          category: 'other',
          assigneeId: ''
        });
        fetchStats();
        fetchTickets();
      }
    } catch (error) {
      console.error('Failed to create ticket:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/tickets/${ticketId}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });
      fetchStats();
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        fetchTicketDetail(ticketId);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/tickets/${selectedTicket.id}/comments`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ content: commentText, isPrivate: isPrivateNote })
      });
      setCommentText('');
      setIsPrivateNote(false);
      fetchTicketDetail(selectedTicket.id);
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-500/20 text-blue-400';
      case 'in_progress': return 'bg-purple-500/20 text-purple-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'on_hold': return 'bg-orange-500/20 text-orange-400';
      case 'resolved': return 'bg-green-500/20 text-green-400';
      case 'closed': return 'bg-slate-500/20 text-slate-400';
      case 'cancelled': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getSLAColor = (slaStatus) => {
    if (!slaStatus) return 'text-slate-400';
    switch (slaStatus.status) {
      case 'breached': return 'text-red-400';
      case 'at_risk': return 'text-orange-400';
      case 'on_track': return 'text-green-400';
      case 'met': return 'text-blue-400';
      default: return 'text-slate-400';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'incident': return AlertTriangle;
      case 'service_request': return HelpCircle;
      case 'problem': return AlertCircle;
      case 'change': return RefreshCw;
      default: return Ticket;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'hardware': return Server;
      case 'software': return FileText;
      case 'network': return Wifi;
      case 'security': return Shield;
      case 'email': return Mail;
      case 'access': return Key;
      default: return HelpCircle;
    }
  };

  const formatTime = (minutes) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours < 24) return `${hours}h ${mins}m`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  };

  return (
    <div className="min-h-screen bg-slate-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl">
                  <Ticket className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">IT Service Desk</h1>
                  <p className="text-slate-400">Manage incidents, requests, and changes</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
              data-testid="create-ticket-btn"
            >
              <Plus className="w-5 h-5" />
              New Ticket
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'tickets', label: 'All Tickets', icon: Ticket },
            { id: 'my-tickets', label: 'My Tickets', icon: User },
            { id: 'team', label: 'Team Workload', icon: Users },
            { id: 'reports', label: 'SLA Reports', icon: PieChart }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                activeTab === tab.id
                  ? 'bg-cyan-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 text-center">
                <div className="text-3xl font-bold text-white">{stats.total}</div>
                <div className="text-sm text-slate-400">Total Tickets</div>
              </div>
              <div className="bg-blue-500/10 rounded-xl border border-blue-500/30 p-4 text-center">
                <div className="text-3xl font-bold text-blue-400">{stats.open}</div>
                <div className="text-sm text-slate-400">Open</div>
              </div>
              <div className="bg-purple-500/10 rounded-xl border border-purple-500/30 p-4 text-center">
                <div className="text-3xl font-bold text-purple-400">{stats.inProgress}</div>
                <div className="text-sm text-slate-400">In Progress</div>
              </div>
              <div className="bg-red-500/10 rounded-xl border border-red-500/30 p-4 text-center">
                <div className="text-3xl font-bold text-red-400">{stats.slaBreached}</div>
                <div className="text-sm text-slate-400">SLA Breached</div>
              </div>
              <div className="bg-green-500/10 rounded-xl border border-green-500/30 p-4 text-center">
                <div className="text-3xl font-bold text-green-400">{stats.resolved}</div>
                <div className="text-sm text-slate-400">Resolved</div>
              </div>
              <div className="bg-slate-500/10 rounded-xl border border-slate-500/30 p-4 text-center">
                <div className="text-3xl font-bold text-slate-400">{stats.closed}</div>
                <div className="text-sm text-slate-400">Closed</div>
              </div>
            </div>

            {/* Today's Activity & Priority Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-cyan-400" />
                  Today's Activity
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-cyan-400">{stats.todayCreated}</div>
                    <div className="text-sm text-slate-400">Created Today</div>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-400">{stats.todayResolved}</div>
                    <div className="text-sm text-slate-400">Resolved Today</div>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-white">{formatTime(stats.avgResponseTime)}</div>
                    <div className="text-sm text-slate-400">Avg Response</div>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-white">{formatTime(stats.avgResolutionTime)}</div>
                    <div className="text-sm text-slate-400">Avg Resolution</div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                  By Priority
                </h3>
                <div className="space-y-3">
                  {[
                    { key: 'critical', label: 'Critical (P1)', color: 'bg-red-500', sla: '1 hour' },
                    { key: 'high', label: 'High (P2)', color: 'bg-orange-500', sla: '4 hours' },
                    { key: 'medium', label: 'Medium (P3)', color: 'bg-yellow-500', sla: '8 hours' },
                    { key: 'low', label: 'Low (P4)', color: 'bg-green-500', sla: '24 hours' }
                  ].map(p => (
                    <div key={p.key} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${p.color}`} />
                        <span className="text-white">{p.label}</span>
                        <span className="text-xs text-slate-500">SLA: {p.sla}</span>
                      </div>
                      <span className="text-xl font-bold text-white">{stats.byPriority?.[p.key] || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Ticket Types */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">By Ticket Type</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: 'incident', label: 'Incidents', icon: AlertTriangle, color: 'text-red-400' },
                  { key: 'service_request', label: 'Service Requests', icon: HelpCircle, color: 'text-blue-400' },
                  { key: 'problem', label: 'Problems', icon: AlertCircle, color: 'text-orange-400' },
                  { key: 'change', label: 'Changes', icon: RefreshCw, color: 'text-purple-400' }
                ].map(t => (
                  <div key={t.key} className="p-4 bg-slate-900/50 rounded-lg text-center">
                    <t.icon className={`w-8 h-8 mx-auto mb-2 ${t.color}`} />
                    <div className="text-2xl font-bold text-white">{stats.byType?.[t.key] || 0}</div>
                    <div className="text-sm text-slate-400">{t.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tickets Tab */}
        {(activeTab === 'tickets' || activeTab === 'my-tickets') && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search tickets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  />
                </div>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              >
                <option value="">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              >
                <option value="">All Priority</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              >
                <option value="">All Types</option>
                <option value="incident">Incident</option>
                <option value="service_request">Service Request</option>
                <option value="problem">Problem</option>
                <option value="change">Change</option>
              </select>
            </div>

            {/* Ticket List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-12 text-center">
                <Ticket className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Tickets Found</h3>
                <p className="text-slate-400 mb-6">Create your first ticket to get started</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg"
                >
                  Create Ticket
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map(ticket => {
                  const TypeIcon = getTypeIcon(ticket.ticketType);
                  return (
                    <div
                      key={ticket.id}
                      onClick={() => fetchTicketDetail(ticket.id)}
                      className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 hover:border-cyan-500/50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <TypeIcon className="w-5 h-5 text-cyan-400" />
                            <span className="font-mono text-sm text-cyan-400">{ticket.ticketNumber}</span>
                            <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(ticket.status)}`}>
                              {ticket.status.replace('_', ' ')}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs border ${getPriorityColor(ticket.priority)}`}>
                              {ticket.priority}
                            </span>
                          </div>
                          <h4 className="text-white font-medium mb-1">{ticket.subject}</h4>
                          <div className="flex items-center gap-4 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {ticket.requesterName}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(ticket.createdAt).toLocaleDateString()}
                            </span>
                            {ticket.assigneeName && (
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {ticket.assigneeName}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm font-medium ${getSLAColor(ticket.slaStatus)}`}>
                            {ticket.slaStatus?.status === 'breached' && `Overdue ${ticket.slaStatus.overdueHours}h`}
                            {ticket.slaStatus?.status === 'at_risk' && 'At Risk'}
                            {ticket.slaStatus?.status === 'on_track' && `${ticket.slaStatus.remainingHours}h left`}
                            {ticket.slaStatus?.status === 'met' && 'SLA Met'}
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-500 mt-2 ml-auto" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Team Workload Tab */}
        {activeTab === 'team' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-400" />
                Team Workload Distribution
              </h3>
              {workload.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No open tickets to display</p>
              ) : (
                <div className="space-y-3">
                  {workload.map((member, idx) => (
                    <div key={idx} className="p-4 bg-slate-900/50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                            <User className="w-5 h-5 text-cyan-400" />
                          </div>
                          <span className="text-white font-medium">{member.assigneeName}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-white">{member.ticketCount}</div>
                          <div className="text-xs text-slate-400">tickets</div>
                        </div>
                      </div>
                      <div className="flex gap-4 text-sm">
                        {member.criticalCount > 0 && (
                          <span className="text-red-400">{member.criticalCount} critical</span>
                        )}
                        {member.highCount > 0 && (
                          <span className="text-orange-400">{member.highCount} high</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SLA Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            {/* Report Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <PieChart className="w-6 h-6 text-cyan-400" />
                  SLA Compliance Report
                </h2>
                <p className="text-slate-400 text-sm mt-1">Track SLA attainment and team performance metrics</p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={slaReportPeriod}
                  onChange={(e) => setSlaReportPeriod(e.target.value)}
                  className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                >
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="quarter">Last 90 Days</option>
                  <option value="year">Last Year</option>
                </select>
                <button
                  onClick={handleDownloadSlaReport}
                  disabled={downloadingPdf || !slaReport}
                  className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white rounded-lg"
                  data-testid="download-sla-report-btn"
                >
                  {downloadingPdf ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                  Download PDF
                </button>
              </div>
            </div>

            {slaReportLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
              </div>
            ) : slaReport ? (
              <>
                {/* Executive Summary */}
                <div className="bg-gradient-to-r from-slate-800/80 to-slate-800/50 rounded-xl border border-slate-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Executive Summary</h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      slaReport.summary?.overallHealth === 'Good' ? 'bg-green-500/20 text-green-400' :
                      slaReport.summary?.overallHealth === 'Needs Attention' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {slaReport.summary?.overallHealth}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                      <div className="text-3xl font-bold text-white">{slaReport.currentPeriod?.total || 0}</div>
                      <div className="text-sm text-slate-400">Total Tickets</div>
                      <div className={`text-xs mt-1 flex items-center justify-center gap-1 ${slaReport.trends?.totalTickets > 0 ? 'text-red-400' : 'text-green-400'}`}>
                        {slaReport.trends?.totalTickets > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {Math.abs(slaReport.trends?.totalTickets || 0)}% vs prev
                      </div>
                    </div>
                    <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                      <div className={`text-3xl font-bold ${
                        (slaReport.currentPeriod?.slaAttainmentRate || 0) >= 90 ? 'text-green-400' :
                        (slaReport.currentPeriod?.slaAttainmentRate || 0) >= 70 ? 'text-yellow-400' :
                        'text-red-400'
                      }`}>{slaReport.currentPeriod?.slaAttainmentRate || 0}%</div>
                      <div className="text-sm text-slate-400">SLA Attainment</div>
                      <div className={`text-xs mt-1 flex items-center justify-center gap-1 ${slaReport.trends?.slaAttainment >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {slaReport.trends?.slaAttainment >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {slaReport.trends?.slaAttainment || 0}% vs prev
                      </div>
                    </div>
                    <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                      <div className="text-3xl font-bold text-white">{slaReport.summary?.avgResponseTimeFormatted || '-'}</div>
                      <div className="text-sm text-slate-400">Avg Response</div>
                      <div className={`text-xs mt-1 flex items-center justify-center gap-1 ${slaReport.trends?.avgResponseTime <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {slaReport.trends?.avgResponseTime <= 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                        {Math.abs(slaReport.trends?.avgResponseTime || 0)}% vs prev
                      </div>
                    </div>
                    <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                      <div className="text-3xl font-bold text-white">{slaReport.summary?.avgResolutionTimeFormatted || '-'}</div>
                      <div className="text-sm text-slate-400">Avg Resolution</div>
                      <div className={`text-xs mt-1 flex items-center justify-center gap-1 ${slaReport.trends?.avgResolutionTime <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {slaReport.trends?.avgResolutionTime <= 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                        {Math.abs(slaReport.trends?.avgResolutionTime || 0)}% vs prev
                      </div>
                    </div>
                  </div>
                </div>

                {/* SLA by Priority */}
                <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-cyan-400" />
                    SLA Performance by Priority
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-sm text-slate-400 border-b border-slate-700">
                          <th className="pb-3 pr-4">Priority</th>
                          <th className="pb-3 pr-4">SLA Target</th>
                          <th className="pb-3 pr-4">Total Resolved</th>
                          <th className="pb-3 pr-4">Within SLA</th>
                          <th className="pb-3 pr-4">Attainment Rate</th>
                          <th className="pb-3">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { key: 'critical', label: 'Critical (P1)', target: '1 hour', color: 'text-red-400' },
                          { key: 'high', label: 'High (P2)', target: '4 hours', color: 'text-orange-400' },
                          { key: 'medium', label: 'Medium (P3)', target: '8 hours', color: 'text-yellow-400' },
                          { key: 'low', label: 'Low (P4)', target: '24 hours', color: 'text-green-400' }
                        ].map(p => {
                          const data = slaReport.currentPeriod?.byPriority?.[p.key] || { total: 0, withinSla: 0, rate: 0 };
                          return (
                            <tr key={p.key} className="border-b border-slate-700/50">
                              <td className={`py-3 pr-4 font-medium ${p.color}`}>{p.label}</td>
                              <td className="py-3 pr-4 text-slate-300">{p.target}</td>
                              <td className="py-3 pr-4 text-white">{data.total}</td>
                              <td className="py-3 pr-4 text-white">{data.withinSla}</td>
                              <td className="py-3 pr-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full ${data.rate >= 90 ? 'bg-green-500' : data.rate >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                      style={{ width: `${data.rate}%` }}
                                    />
                                  </div>
                                  <span className="text-white font-medium">{data.rate}%</span>
                                </div>
                              </td>
                              <td className="py-3">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  data.rate >= 90 ? 'bg-green-500/20 text-green-400' :
                                  data.rate >= 70 ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-red-500/20 text-red-400'
                                }`}>
                                  {data.rate >= 90 ? 'On Target' : data.rate >= 70 ? 'At Risk' : 'Breached'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Ticket Distribution & Team Performance */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* By Type */}
                  <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Ticket Distribution by Type</h3>
                    <div className="space-y-3">
                      {[
                        { key: 'incident', label: 'Incidents', icon: AlertTriangle, color: 'bg-red-500' },
                        { key: 'service_request', label: 'Service Requests', icon: HelpCircle, color: 'bg-blue-500' },
                        { key: 'problem', label: 'Problems', icon: AlertCircle, color: 'bg-orange-500' },
                        { key: 'change', label: 'Changes', icon: RefreshCw, color: 'bg-purple-500' }
                      ].map(t => {
                        const count = slaReport.currentPeriod?.byType?.[t.key] || 0;
                        const total = slaReport.currentPeriod?.total || 1;
                        const pct = Math.round((count / total) * 100);
                        return (
                          <div key={t.key} className="flex items-center gap-3">
                            <t.icon className="w-5 h-5 text-slate-400" />
                            <div className="flex-1">
                              <div className="flex justify-between mb-1">
                                <span className="text-slate-300">{t.label}</span>
                                <span className="text-white font-medium">{count} ({pct}%)</span>
                              </div>
                              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                <div className={`h-full ${t.color} rounded-full`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Team Performance */}
                  <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Team Performance</h3>
                    {slaReport.currentPeriod?.byAssignee?.length > 0 ? (
                      <div className="space-y-3">
                        {slaReport.currentPeriod.byAssignee.slice(0, 5).map((member, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-medium">
                                {idx + 1}
                              </div>
                              <span className="text-white">{member.name}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-white font-medium">{member.ticketsResolved} resolved</div>
                              <div className="text-xs text-slate-400">Avg: {formatTime(member.avgResolutionMinutes)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-center py-8">No resolution data available</p>
                    )}
                  </div>
                </div>

                {/* Report Period Info */}
                <div className="bg-slate-800/30 rounded-xl border border-slate-700/50 p-4 text-sm text-slate-400">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span>Report Period: {slaReport.reportPeriod?.startDate} to {slaReport.reportPeriod?.endDate}</span>
                    <span>Generated: {new Date(slaReport.generatedAt).toLocaleString()}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-12 text-center">
                <PieChart className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Report Data</h3>
                <p className="text-slate-400">No tickets found for the selected period</p>
              </div>
            )}
          </div>
        )}

        {/* Create Ticket Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-cyan-400" />
                  Create New Ticket
                </h2>
                <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleCreateTicket} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Subject *</label>
                  <input
                    type="text"
                    value={ticketForm.subject}
                    onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                    required
                    placeholder="Brief description of the issue"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Type</label>
                    <select
                      value={ticketForm.ticketType}
                      onChange={(e) => setTicketForm({ ...ticketForm, ticketType: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                    >
                      <option value="incident">Incident</option>
                      <option value="service_request">Service Request</option>
                      <option value="problem">Problem</option>
                      <option value="change">Change Request</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Priority</label>
                    <select
                      value={ticketForm.priority}
                      onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                    >
                      <option value="low">Low (P4) - 24h SLA</option>
                      <option value="medium">Medium (P3) - 8h SLA</option>
                      <option value="high">High (P2) - 4h SLA</option>
                      <option value="critical">Critical (P1) - 1h SLA</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Category</label>
                  <select
                    value={ticketForm.category}
                    onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  >
                    <option value="hardware">Hardware</option>
                    <option value="software">Software</option>
                    <option value="network">Network</option>
                    <option value="security">Security</option>
                    <option value="access">Access/Permissions</option>
                    <option value="email">Email</option>
                    <option value="account">Account</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Assign To</label>
                  <select
                    value={ticketForm.assigneeId}
                    onChange={(e) => setTicketForm({ ...ticketForm, assigneeId: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  >
                    <option value="">Unassigned</option>
                    {teamMembers.map(member => (
                      <option key={member.id} value={member.id}>{member.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Description *</label>
                  <textarea
                    value={ticketForm.description}
                    onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                    required
                    rows={4}
                    placeholder="Detailed description of the issue..."
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white rounded-lg flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                  Create Ticket
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Ticket Detail Modal */}
        {showTicketModal && selectedTicket && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-4xl border border-slate-700 max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-cyan-400">{selectedTicket.ticketNumber}</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(selectedTicket.status)}`}>
                    {selectedTicket.status.replace('_', ' ')}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs border ${getPriorityColor(selectedTicket.priority)}`}>
                    {selectedTicket.priority}
                  </span>
                </div>
                <button onClick={() => setShowTicketModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <h2 className="text-xl font-bold text-white mb-4">{selectedTicket.subject}</h2>
                
                {/* Meta Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <div className="text-xs text-slate-400 mb-1">Requester</div>
                    <div className="text-white">{selectedTicket.requesterName}</div>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <div className="text-xs text-slate-400 mb-1">Assigned To</div>
                    <div className="text-white">{selectedTicket.assigneeName || 'Unassigned'}</div>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <div className="text-xs text-slate-400 mb-1">Category</div>
                    <div className="text-white capitalize">{selectedTicket.category}</div>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <div className="text-xs text-slate-400 mb-1">SLA Status</div>
                    <div className={getSLAColor(selectedTicket.slaStatus)}>
                      {selectedTicket.slaStatus?.status?.replace('_', ' ').toUpperCase()}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-slate-400 mb-2">Description</h3>
                  <div className="p-4 bg-slate-900/50 rounded-lg text-white whitespace-pre-wrap">
                    {selectedTicket.description}
                  </div>
                </div>

                {/* Status Actions */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-slate-400 mb-2">Quick Actions</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedTicket.status === 'open' && (
                      <button
                        onClick={() => handleStatusChange(selectedTicket.id, 'in_progress')}
                        className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm"
                      >
                        <PlayCircle className="w-4 h-4" /> Start Working
                      </button>
                    )}
                    {selectedTicket.status === 'in_progress' && (
                      <>
                        <button
                          onClick={() => handleStatusChange(selectedTicket.id, 'pending')}
                          className="flex items-center gap-2 px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm"
                        >
                          <PauseCircle className="w-4 h-4" /> Mark Pending
                        </button>
                        <button
                          onClick={() => handleStatusChange(selectedTicket.id, 'resolved')}
                          className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm"
                        >
                          <CheckCircle2 className="w-4 h-4" /> Resolve
                        </button>
                      </>
                    )}
                    {selectedTicket.status === 'pending' && (
                      <button
                        onClick={() => handleStatusChange(selectedTicket.id, 'in_progress')}
                        className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm"
                      >
                        <PlayCircle className="w-4 h-4" /> Resume
                      </button>
                    )}
                    {selectedTicket.status === 'resolved' && (
                      <>
                        <button
                          onClick={() => handleStatusChange(selectedTicket.id, 'closed')}
                          className="flex items-center gap-2 px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white rounded-lg text-sm"
                        >
                          <XCircle className="w-4 h-4" /> Close
                        </button>
                        <button
                          onClick={() => handleStatusChange(selectedTicket.id, 'open')}
                          className="flex items-center gap-2 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm"
                        >
                          <RotateCcw className="w-4 h-4" /> Reopen
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Comments */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-slate-400 mb-2">
                    Comments ({selectedTicket.comments?.length || 0})
                  </h3>
                  <div className="space-y-3 mb-4">
                    {selectedTicket.comments?.map(comment => (
                      <div
                        key={comment.id}
                        className={`p-3 rounded-lg ${comment.isPrivate ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-slate-900/50'}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white font-medium text-sm">{comment.userName}</span>
                          <div className="flex items-center gap-2">
                            {comment.isPrivate && (
                              <span className="text-xs text-yellow-400">Private Note</span>
                            )}
                            <span className="text-xs text-slate-500">
                              {new Date(comment.createdAt).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <p className="text-slate-300 text-sm">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add Comment Form */}
                  <form onSubmit={handleAddComment} className="space-y-2">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Add a comment..."
                      rows={3}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white resize-none"
                    />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-slate-400">
                        <input
                          type="checkbox"
                          checked={isPrivateNote}
                          onChange={(e) => setIsPrivateNote(e.target.checked)}
                          className="rounded"
                        />
                        Private note (IT team only)
                      </label>
                      <button
                        type="submit"
                        disabled={submitting || !commentText.trim()}
                        className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white rounded-lg text-sm"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Add Comment
                      </button>
                    </div>
                  </form>
                </div>

                {/* Audit Log */}
                {selectedTicket.auditLogs?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-400 mb-2 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Activity Log
                    </h3>
                    <div className="space-y-2">
                      {selectedTicket.auditLogs.slice(0, 5).map(log => (
                        <div key={log.id} className="flex items-center gap-3 p-2 bg-slate-900/50 rounded text-sm">
                          <div className="w-2 h-2 rounded-full bg-cyan-500" />
                          <span className="text-slate-400">{new Date(log.timestamp).toLocaleString()}</span>
                          <span className="text-white">{log.action.replace('_', ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketDashboard;
